import { Cvss4P0 } from 'ae-cvss-calculator';
import cvssV3Calculator = require('cvss');
import * as _ from 'lodash';

import { VulnSeverity } from '../issuesV2';
import type { CurrentRawVuln, SecurityRuleLevel } from '../model-typings';

import type { VulnSeverityBounds } from './types';

const CVSS_V2_SCORE_MAP = {
  AV: {
    L: 0.395,
    A: 0.646,
    N: 1.0,
  },
  AC: {
    H: 0.35,
    M: 0.61,
    L: 0.71,
  },
  AU: {
    M: 0.45,
    S: 0.56,
    N: 0.704,
  },
  C: {
    N: 0,
    P: 0.275,
    C: 0.660,
  },
  I: {
    N: 0,
    P: 0.275,
    C: 0.660,
  },
  A: {
    N: 0,
    P: 0.275,
    C: 0.660,
  },
};

const groupCvssSections = (cvssVector: string): RegExpExecArray | null => {
  const cvssV2Regex = /^AV:([LAN])\/AC:([HML])\/AU:([MSN])\/C:([NPC])\/I:([NPC])\/A:([NPC])$/i;
  return cvssV2Regex.exec(cvssVector);
};

// https://www.first.org/cvss/v2/guide
// We only care about base score here
// BaseScore = round_to_1_decimal(((0.6*Impact)+(0.4*Exploitability)-1.5)*f(Impact))
// f(impact)= 0 if Impact=0, 1.176 otherwise
const getCvssV2Score = (cvssVector: string) => {
  if (!_.isString(cvssVector)) {
    return null;
  }
  const cvssGroups = groupCvssSections(cvssVector.toUpperCase());
  if (!cvssGroups) {
    return null;
  }

  const exploitability =
    20 *
    CVSS_V2_SCORE_MAP.AV[cvssGroups[1] as keyof typeof CVSS_V2_SCORE_MAP['AV']] *
    CVSS_V2_SCORE_MAP.AC[cvssGroups[2] as keyof typeof CVSS_V2_SCORE_MAP['AC']] *
    CVSS_V2_SCORE_MAP.AU[cvssGroups[3] as keyof typeof CVSS_V2_SCORE_MAP['AU']];

  const impact =
    10.41 *
    (1 -
      (1 - CVSS_V2_SCORE_MAP.C[cvssGroups[4] as keyof typeof CVSS_V2_SCORE_MAP['C']]) *
        (1 - CVSS_V2_SCORE_MAP.I[cvssGroups[5] as keyof typeof CVSS_V2_SCORE_MAP['I']]) *
        (1 - CVSS_V2_SCORE_MAP.A[cvssGroups[6] as keyof typeof CVSS_V2_SCORE_MAP['A']]));
  const fImpact = impact === 0 ? 0 : 1.176;

  const baseScore = (((0.6 * impact) + (0.4 * exploitability) - 1.5) * fImpact);

  return Math.round(baseScore * 10) / 10;
};

const convertCvss4VectorToScore = (cvssV4: string) => {
  let cvss: number | null = null;
  try {
    let cvssVector = cvssV4;
    if (!cvssVector.startsWith('CVSS:4.0/')) {
      cvssVector = `CVSS:4.0/${cvssVector}`;
    }
    const v4VectorCalculator = new Cvss4P0(cvssVector);
    cvss = v4VectorCalculator.calculateOverallScore();
  } catch (err) {
    cvss = null;
  }
  return cvss;
};

const convertCvssVectorToScore = (cvssVector: string): number | null => {
  let cvss;
  try {
    if (groupCvssSections(cvssVector)) {
      cvss = getCvssV2Score(cvssVector);
    } else {
      let updatedCvssVector = cvssVector;
      if (!cvssVector.startsWith('CVSS:3.0/')) {
        updatedCvssVector = `CVSS:3.0/${cvssVector}`;
      }
      cvss = cvssV3Calculator.getScore(updatedCvssVector);
    }
  } catch (er) {
    cvss = null;
  }
  return cvss;
};

type CvssRawVuln = Pick<CurrentRawVuln, 'cvssV4Vector' | 'cvssV3Vector' | 'cvssV2Vector'>;

export const getCVSSScore = (rawVuln: CvssRawVuln): number | null => {
  let cvss: number | null = null;
  if (rawVuln.cvssV4Vector) {
    cvss = convertCvss4VectorToScore(rawVuln.cvssV4Vector);
  }
  if (!cvss && rawVuln.cvssV3Vector) {
    cvss = convertCvssVectorToScore(rawVuln.cvssV3Vector);
  }
  if (!cvss && rawVuln.cvssV2Vector) {
    cvss = convertCvssVectorToScore(rawVuln.cvssV2Vector);
  }
  return cvss;
};

// getVulnScoreFromSeverity takes in a VulnSeverityLevel, and
// returns the minimum CVSS score that technically falls under this severity
export const getVulnScoreFromSeverity = (vulnLevel: SecurityRuleLevel): number => {
  switch (vulnLevel) {
    case 'CRITICAL': return 9;
    case 'HIGH': return 7;
    case 'MEDIUM': return 4;
    case 'LOW':
    default:
      return 0;
  }
};

/**
 * Given a CVSS score (or any number), determine its severity level.
 */
export const getSeverityFromVulnScore = (cvss: number): VulnSeverity => {
  if (cvss >= 9) {
    return VulnSeverity.Critical;
  }
  if (cvss >= 7) {
    return VulnSeverity.High;
  }
  if (cvss >= 4) {
    return VulnSeverity.Medium;
  }
  if (cvss > 0) {
    return VulnSeverity.Low;
  }
  return VulnSeverity.Unknown;
};

export const getCVSSV2Regex = () => /^AV:([NAL])\/AC:([LMH])\/A[Uu]:([NSM])\/C:([NPC])\/I:([NPC])\/A:([NPC])$/;
export const getCVSSV3Regex = () => /^(?:CVSS:3.\d\/)?AV:([NALP])\/AC:([LH])\/PR:([NLH])\/UI:([NR])\/S:([UC])\/C:([NLH])\/I:([NLH])\/A:([NLH])$/;

// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
export type CVSSMetricMap = {
  [shortname: string]: {
    title: string;
    index: number; // Match index
    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    scores: {
      [shortname: string]: string;
    };
  };
}

/**
 * CVSSv4BaseMetricNames is a map of CVSSv4 metric names to their corresponding values.
 * https://www.first.org/cvss/v4-0/specification-document
 */
export const CVSSv4BaseMetricNames: CVSSMetricMap = {
  AV: {
    title: 'Attack Vector',
    index: 1,
    scores: {
      N: 'Network',
      A: 'Adjacent Network',
      L: 'Local',
      P: 'Physical',
    },
  },
  AC: {
    title: 'Attack Complexity',
    index: 2,
    scores: {
      H: 'High',
      L: 'Low',
    },
  },
  AT: {
    title: 'Attack Requirements',
    index: 3,
    scores: {
      N: 'None',
      P: 'Present',
    },
  },
  PR: {
    title: 'Privileges Required',
    index: 3,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  UI: {
    title: 'User Interaction',
    index: 4,
    scores: {
      N: 'None',
      P: 'Passive',
      A: 'Active',
    },
  },
  VC: {
    title: 'Confidentiality Impact to the Vulnerable System',
    index: 5,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  VI: {
    title: 'Integrity Impact to the Vulnerable System',
    index: 6,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  VA: {
    title: 'Availability Impact to the Vulnerable System',
    index: 7,
    scores: {
      H: 'High',
      L: 'Low',
      N: 'None',
    },
  },
};

export const CVSSv3BaseMetricNames: CVSSMetricMap = {
  AV: {
    title: 'Attack Vector',
    index: 1,
    scores: {
      N: 'Network',
      A: 'Adjacent Network',
      L: 'Local',
      P: 'Physical',
    },
  },
  AC: {
    title: 'Attack Complexity',
    index: 2,
    scores: {
      H: 'High',
      L: 'Low',
    },
  },
  PR: {
    title: 'Privileges Required',
    index: 3,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  UI: {
    title: 'User Interaction',
    index: 4,
    scores: {
      N: 'None',
      R: 'Required',
    },
  },
  S: {
    title: 'Scope',
    index: 5,
    scores: {
      U: 'Unchanged',
      C: 'Changed',
    },
  },
  C: {
    title: 'Confidentiality Impact',
    index: 6,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  I: {
    title: 'Integrity Impact',
    index: 7,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
  A: {
    title: 'Availability Impact',
    index: 8,
    scores: {
      N: 'None',
      L: 'Low',
      H: 'High',
    },
  },
};

export const CVSSv2BaseMetricNames: CVSSMetricMap = {
  AV: {
    title: 'Attack Vector',
    index: 1,
    scores: {
      L: 'Local',
      A: 'Adjacent Network',
      N: 'Network',
    },
  },
  AC: {
    title: 'Access Complexity',
    index: 2,
    scores: {
      H: 'High',
      M: 'Medium',
      L: 'Low',
    },
  },
  AU: {
    title: 'Authentication',
    index: 3,
    scores: {
      M: 'Multiple',
      S: 'Single',
      N: 'None',
    },
  },
  C: {
    title: 'Confidentiality Impact',
    index: 4,
    scores: {
      N: 'None',
      P: 'Partial',
      C: 'Complete',
    },
  },
  I: {
    title: 'Integrity Impact',
    index: 5,
    scores: {
      N: 'None',
      P: 'Partial',
      C: 'Complete',
    },
  },
  A: {
    title: 'Availability Impact',
    index: 6,
    scores: {
      N: 'None',
      P: 'Partial',
      C: 'Complete',
    },
  },
};

export type CVSSMetrics = Record<string, string | undefined>

export const getCVSSV2HumanReadableMetrics = (cvssV2: string): CVSSMetrics | null => {
  const parsedCvssV2 = cvssV2.match(getCVSSV2Regex());

  if (!parsedCvssV2) {
    return null;
  }

  return _.mapValues(CVSSv2BaseMetricNames, (val) => val.scores[parsedCvssV2[val.index] as string]);
};

export const getCVSSV3HumanReadableMetrics = (cvssV3: string): CVSSMetrics | null => {
  const parsedCvssV3 = cvssV3.match(getCVSSV3Regex());

  if (!parsedCvssV3) {
    return null;
  }

  return _.mapValues(CVSSv3BaseMetricNames, (val) => val.scores[parsedCvssV3[val.index] as string]);
};

export const getCVSSV4HumanReadableMetrics = (cvssV4: string): CVSSMetrics | null => {
  const parsedV4 = new Cvss4P0(cvssV4);
  return Object.entries(CVSSv4BaseMetricNames).reduce((acc, [componentKey]) => {
    const component = parsedV4.getComponentByStringOpt(componentKey);

    // If an invalid component key is passed in, it will return `null`. Just set as undefined.
    if (!component) {
      acc[componentKey] = undefined;
      return acc;
    }

    /*
     * If it was not in the vector, it will return
     *
     * {
     *  shortName: 'X',
     *  name: 'Not Defined',
     *  abbreviatedName: 'Not Def.',
     *  jsonSchemaName: 'NOT_DEFINED',
     *  description: 'Component is not defined.'
     * }
     *
     * We will check for this and set as undefined.
     */
    if (component.shortName === 'X') {
      acc[componentKey] = undefined;
      return acc;
    }

    /**
     * If it was in the vector, it will return an object like
     * {
     *   shortName: 'P',
     *   name: 'Physical',
     *   abbreviatedName: 'Phys.',
     *   jsonSchemaName: 'PHYSICAL',
     *   description: 'The attack requires the attacker to physically touch or manipulate the vulnerable system. Physical interaction may be brief (e.g., evil maid attack) or persistent. An example of such an attack is a cold boot attack in which an attacker gains access to disk encryption keys after physically accessing the target system. Other examples include peripheral attacks via FireWire/USB Direct Memory Access (DMA).'
     * }
     *
     * We'll just grab the name from this object.
     */
    acc[componentKey] = component.name;
    return acc;
  }, {} as CVSSMetrics);
};

/**
 * VULN_SEVERITY_BOUNDS connects vulnerability severities to their CVSS score limits,
 * making it easier to build knex queries to filter vulnerabilities based on severity.
 * Each severity has 'high' and 'low' limits with comparison operators (op) and
 * CVSS scores. The 'op' property defines the comparison (e.g., '>=', '<=', '<', '>')
 * used in the knex query conditions.
 */
export const VULN_SEVERITY_BOUNDS = {
  [VulnSeverity.Critical]: {
    high: { op: '<=', value: 10 },
    low: { op: '>=', value: 9 },
  },
  [VulnSeverity.High]: {
    high: { op: '<', value: 9 },
    low: { op: '>=', value: 7 },
  },
  [VulnSeverity.Medium]: {
    high: { op: '<', value: 7 },
    low: { op: '>=', value: 4 },
  },
  [VulnSeverity.Low]: {
    high: { op: '<', value: 4 },
    low: { op: '>', value: 0 },
  },
} as Record<VulnSeverity, VulnSeverityBounds>;
